import { HubExchangeAccountBalances, HubUserCredentials, WalletType, WalletTypeV1 } from "@cede/types";
import { API_ENDPOINT, ApiHelper, CEDE_CEXS, getPortfolioBalancesQueryKey } from "@cede/utils";

export { getPortfolioBalancesQueryKey };

export const BALANCE_STALE_TIME = 30 * 1000; // 30 seconds

// Exchanges that don't support the standard getBalances endpoint
// These use different APIs (e.g., polymarket uses prediction market APIs)
export const EXCHANGES_WITHOUT_BALANCE_SUPPORT = new Set(["polymarket"]);

/**
 * On-chain DEXes (perp platforms). Used to:
 *   1. classify portfolio accounts as "dex" vs "cex" (drives `walletAddress: defiAddress`),
 *   2. skip the `walletTypes` filter on `getBalances` so the SDK enumerates all wallets.
 *
 * Keep this list explicit. Do NOT derive it from `SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES`
 * — that list also contains CEX-perps exchanges like Bitget, which have both spot and
 * futures markets and need an explicit `walletTypes` filter (gods2-tte broke from this).
 */
export const DEX_EXCHANGE_IDS: ReadonlySet<string> = new Set([
  CEDE_CEXS.HYPERLIQUID,
  CEDE_CEXS.TRADEXYZ,
  CEDE_CEXS.PARADEX,
  CEDE_CEXS.HYNA,
  CEDE_CEXS.CASH,
  CEDE_CEXS.GRVT,
  CEDE_CEXS.ZEROONE,
  CEDE_CEXS.EXTENDED,
  CEDE_CEXS.HIBACHI,
  CEDE_CEXS.DECIBEL,
  CEDE_CEXS.HOTSTUFF,
  CEDE_CEXS.KATANA,
]);

export const getWalletTypesForExchange = (exchangeId: string): string[] => {
  // Bybit uses UNIFIED wallet, others use SPOT
  if (exchangeId === CEDE_CEXS.BYBIT) return [WalletType.UNIFIED];
  if (exchangeId === CEDE_CEXS.KRAKEN_FUTURES) return [WalletType.FUTURES];

  // DEX perps platforms — don't pass walletTypes so the SDK returns all available
  if (DEX_EXCHANGE_IDS.has(exchangeId)) return [];

  return [WalletTypeV1.SPOT];
};

export const fetchAccountBalances = (
  apiHelper: ApiHelper,
  auth: HubUserCredentials,
  accountId: number,
  walletTypes: string[],
): Promise<HubExchangeAccountBalances> => {
  const queryParams: Record<string, string> = {
    accountId: String(accountId),
    forceRefresh: "false",
  };
  // Only pass walletTypes if specified — DEX exchanges omit it so the SDK returns all available
  if (walletTypes.length > 0) {
    queryParams.walletTypes = walletTypes.join(",");
  }
  return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_BALANCES, {
    queryParams,
  });
};

export const BALANCE_QUERY_OPTIONS = {
  staleTime: BALANCE_STALE_TIME,
  refetchInterval: BALANCE_STALE_TIME,
  refetchOnWindowFocus: true,
} as const;
